import React from 'react';
import { useNavigate } from 'react-router-dom';
import { LockKeyhole, Moon, ShieldCheck, Sun, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { Button, Card, CardBody, Field, Input } from '@/components/ui/primitives';
import { useAuth } from '@/lib/auth';
import { ApiError } from '@/lib/api';
import { useTheme } from '@/lib/theme';

export function LoginPage(): JSX.Element {
  const { login, user, authEnabled } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [username, setUsername] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const signedIn = await login(username, password);
      toast.success(`Signed in as ${signedIn.displayName}`);
      navigate('/', { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Sign in failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative flex min-h-screen overflow-hidden bg-background">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_15%_15%,hsl(var(--primary)/0.10),transparent_28rem),radial-gradient(circle_at_85%_80%,hsl(var(--primary)/0.06),transparent_32rem)]" />

      <button
        type="button"
        onClick={toggleTheme}
        className="absolute right-5 top-5 z-20 flex h-9 w-9 items-center justify-center rounded-xl border border-border bg-card text-muted-foreground shadow-sm transition-colors hover:bg-accent hover:text-foreground"
        aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
      >
        {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
      </button>

      <section className="relative hidden flex-1 flex-col justify-between border-r border-border/60 bg-surface/45 p-10 backdrop-blur-sm lg:flex">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 ring-1 ring-primary/15">
            <img src="/brand/leadforge-mark-64.png" alt="" className="h-8 w-8 object-contain" />
          </div>
          <div>
            <p className="text-lg font-extrabold tracking-tight">LeadForge</p>
            <p className="text-xs text-muted-foreground">Local CRM</p>
          </div>
        </div>

        <div className="max-w-lg">
          <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <Zap className="h-5 w-5" />
          </div>
          <h1 className="text-4xl font-extrabold leading-tight tracking-tight">
            Leads, calls, follow-ups.<br />One focused workspace.
          </h1>
          <p className="mt-4 max-w-md text-sm leading-6 text-muted-foreground">
            LeadForge keeps the sales workflow fast without turning your local CRM into a maze of screens and admin clutter.
          </p>
          <div className="mt-8 flex items-center gap-2 text-xs font-semibold text-muted-foreground">
            <ShieldCheck className="h-4 w-4 text-emerald-500" />
            Authentication and role permissions are enforced by the local server.
          </div>
        </div>

        <p className="text-2xs text-muted-foreground">Private by design · running on your machine</p>
      </section>

      <section className="relative z-10 flex w-full items-center justify-center p-6 lg:w-[34rem] lg:p-10">
        <div className="w-full max-w-sm">
          <div className="mb-6 flex items-center gap-3 lg:hidden">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 ring-1 ring-primary/15">
              <img src="/brand/leadforge-mark-64.png" alt="" className="h-7 w-7 object-contain" />
            </div>
            <div><p className="text-base font-extrabold tracking-tight">LeadForge</p><p className="text-2xs text-muted-foreground">Local CRM</p></div>
          </div>

          <Card className="overflow-hidden border-border/80 shadow-xl shadow-black/[0.04] dark:shadow-black/20">
            <CardBody className="p-6">
              <div className="mb-6">
                <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary"><LockKeyhole className="h-4 w-4" /></div>
                <h2 className="text-xl font-extrabold tracking-tight">Welcome back</h2>
                <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                  {user
                    ? `Already signed in as ${user.displayName}.`
                    : authEnabled
                      ? 'Sign in to continue to LeadForge.'
                      : 'Sign in to attribute your work. Authentication is not required yet.'}
                </p>
              </div>

              <form onSubmit={submit} className="space-y-4">
                <Field label="Username">
                  <Input
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    autoComplete="username"
                    autoFocus
                    required
                    placeholder="your username"
                  />
                </Field>
                <Field label="Password">
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    required
                    placeholder="••••••••••••"
                  />
                </Field>

                {error && <div className="rounded-lg border border-destructive/20 bg-destructive/5 px-3 py-2 text-xs font-medium text-destructive">{error}</div>}

                <Button type="submit" className="w-full" loading={busy} disabled={!username || !password}>
                  Sign in
                </Button>
              </form>

              {!authEnabled && (
                <p className="mt-4 text-center text-xs text-muted-foreground">
                  Authentication is currently optional.{' '}
                  <button type="button" className="font-semibold text-primary hover:underline" onClick={() => navigate('/')}>
                    Continue without signing in
                  </button>
                </p>
              )}
            </CardBody>
          </Card>
        </div>
      </section>
    </div>
  );
}
